"use client"

import { SVGProps, useEffect, useMemo, useRef, useState } from "react"
import { motion } from "motion/react"
import { ChevronDown, ChevronUp, Menu as MenuIcon, MessageSquare, Plus, X } from "lucide-react"
import { Button } from "./Button"
import { ChatSettingsDialog } from "./chat-settings-dialog"
import { Dialog, DialogPopup } from "./Dialog"
import { Tabs, TabsList, TabsTab } from "./tabs"
import { useSidebar } from "./sidebar"
import { useIsMobile } from "@/hooks/common/useMobile"
import { useChatPanel } from "@/contexts/ChatPanelContext"
import { AppChatComposer } from "@/components/ui/app-chat/AppChatComposer"
import { AppChatConversation } from "@/components/ui/app-chat/AppChatConversation"
import { AppChatSessionList } from "@/components/ui/app-chat/AppChatSessionList"
import { cn } from "@/lib/utils"
import type { ChatDockAnchor, ChatLayoutMode, Message } from "@/types/chat.types"

interface ChatPanelProps {
  className?: string
  /** Demo mode: override internal messages with externally provided ones */
  demoMessages?: Message[]
  /** Demo mode: replace input area with an action button */
  demoAction?: { label: string; onClick: () => void }
  /** Demo mode: show loading dots */
  demoLoading?: boolean
}

interface DemoProps {
  demoMessages?: Message[]
  demoAction?: { label: string; onClick: () => void }
  demoLoading?: boolean
}

const CHAT_HEIGHTS = {
  medium: 450,
  large: 600,
}

export const CHAT_BOTTOM_OFFSET = 16

function LayoutModeToggle({
  mode,
  onChange,
}: {
  mode: ChatLayoutMode
  onChange: (mode: ChatLayoutMode) => void
}) {
  const modes: ChatLayoutMode[] = ["dock", "sidebar", "modal"]

  return (
    <Tabs value={mode} onValueChange={(value) => onChange(value as ChatLayoutMode)}>
      <TabsList variant="pill" className="w-full sm:w-auto" indicatorColor="secondary">
        {modes.map((nextMode) => (
          <TabsTab
            key={nextMode}
            value={nextMode}
            className="min-w-14 text-[11px]"
          >
            {nextMode === "dock" ? "Dock" : nextMode === "sidebar" ? "Side" : "Modal"}
          </TabsTab>
        ))}
      </TabsList>
    </Tabs>
  )
}

function DockAnchorToggle({
  anchor,
  onChange,
}: {
  anchor: ChatDockAnchor
  onChange: (anchor: ChatDockAnchor) => void
}) {
  const anchors: ChatDockAnchor[] = ["left", "center", "right"]

  return (
    <Tabs value={anchor} onValueChange={(value) => onChange(value as ChatDockAnchor)}>
      <TabsList variant="pill" indicatorColor="secondary">
        {anchors.map((nextAnchor) => (
          <TabsTab
            key={nextAnchor}
            value={nextAnchor}
            className="min-w-12 text-[11px] capitalize"
          >
            {nextAnchor}
          </TabsTab>
        ))}
      </TabsList>
    </Tabs>
  )
}

function PanelChrome({
  title,
  subtitle,
  showDockControls,
  isHistoryOpen,
  onToggleHistory,
  onNewChat,
  onClose,
}: {
  title: string
  subtitle?: string
  showDockControls: boolean
  isHistoryOpen: boolean
  onToggleHistory: () => void
  onNewChat: () => void
  onClose: () => void
}) {
  const {
    layoutMode,
    setLayoutMode,
    dockAnchor,
    setDockAnchor,
    panelSize,
    togglePanelSize,
  } = useChatPanel()

  return (
    <div className="shrink-0 border-b border-border px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className={cn("h-8 w-8 p-0", isHistoryOpen && "bg-muted")}
            onClick={onToggleHistory}
          >
            <MenuIcon className="size-4" />
          </Button>
          <div className="flex size-7 items-center justify-center rounded-md bg-foreground">
            <img
              src="/img/logo.png"
              alt="EqualIQ"
              className="size-4 object-contain invert dark:invert-0"
            />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{title}</p>
            {subtitle && (
              <p className="truncate text-[11px] text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={onNewChat}
          >
            <Plus className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={togglePanelSize}
          >
            {panelSize === "large" ? <ChevronDown className="size-4" /> : <ChevronUp className="size-4" />}
          </Button>
          <ChatSettingsDialog />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <LayoutModeToggle mode={layoutMode} onChange={setLayoutMode} />
        {showDockControls && (
          <DockAnchorToggle anchor={dockAnchor} onChange={setDockAnchor} />
        )}
      </div>
    </div>
  )
}

function PanelBody({
  isHistoryOpen,
  onHistoryOpenChange,
  onClose,
  showDockControls,
  demoMessages,
  demoAction,
  demoLoading,
}: {
  isHistoryOpen: boolean
  onHistoryOpenChange: (open: boolean) => void
  onClose: () => void
  showDockControls: boolean
} & DemoProps) {
  const {
    sessions,
    currentSessionId,
    selectSession,
    deleteSession,
    createSession,
    currentSession,
    messages,
    isCurrentSessionLoading,
    settings,
    currentDraft,
    setCurrentDraft,
    currentScope,
    setCurrentScope,
    contextItems,
    setContextItems,
    removeContextItem,
    dynamicPageItems,
    sendMessage,
    progressFrames,
  } = useChatPanel()

  const isDemoMode = !!demoMessages
  const displayMessages = demoMessages ?? messages
  const displayLoading = isDemoMode ? (demoLoading ?? false) : isCurrentSessionLoading

  const placeholder = "Ask anything about your deals..."

  const title = isHistoryOpen ? "Chat History" : "AI Assistant"
  const subtitle = isHistoryOpen
    ? `${sessions.length} conversation${sessions.length === 1 ? "" : "s"}`
    : isDemoMode
      ? "Demo Guide"
      : currentSession.title === "New Chat"
        ? "Shared across your chat surfaces"
        : currentSession.title

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-3xl border border-border bg-background/95 shadow-sm backdrop-blur-sm">
      <PanelChrome
        title={title}
        subtitle={subtitle}
        showDockControls={showDockControls}
        isHistoryOpen={isHistoryOpen}
        onToggleHistory={() => onHistoryOpenChange(!isHistoryOpen)}
        onNewChat={() => {
          createSession(currentScope)
          onHistoryOpenChange(false)
        }}
        onClose={onClose}
      />

      {isHistoryOpen && !isDemoMode ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          <AppChatSessionList
            sessions={sessions}
            currentSessionId={currentSessionId}
            onSelectSession={(sessionId) => {
              selectSession(sessionId)
              onHistoryOpenChange(false)
            }}
            onDeleteSession={deleteSession}
            variant="panel"
          />
        </div>
      ) : (
        <>
          <AppChatConversation
            messages={displayMessages}
            settings={settings}
            isLoading={displayLoading}
            progressFrames={isDemoMode ? undefined : progressFrames}
            variant="panel"
            emptyState={(
              <div className="space-y-2 text-center text-sm text-muted-foreground">
                <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-muted">
                  <img
                    src="/img/logo.png"
                    alt="EqualIQ"
                    className="size-6 object-contain opacity-50"
                  />
                </div>
                <p>Ask anything about your deals</p>
              </div>
            )}
          />
          {isDemoMode && demoAction && (
            <div className="shrink-0 px-3 py-2">
              <button
                onClick={demoAction.onClick}
                className="w-full inline-flex items-center justify-center gap-2 text-xs font-medium px-3 py-2 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                {demoAction.label}
              </button>
            </div>
          )}
          <AppChatComposer
            draft={isDemoMode ? "" : currentDraft}
            onDraftChange={isDemoMode ? () => {} : setCurrentDraft}
            onSubmit={isDemoMode ? () => {} : () => sendMessage()}
            isLoading={displayLoading}
            scope={currentScope}
            onScopeChange={isDemoMode ? () => {} : setCurrentScope}
            contextItems={contextItems}
            onContextItemsChange={isDemoMode ? () => {} : setContextItems}
            onRemoveContextItem={isDemoMode ? () => {} : removeContextItem}
            extraContextItems={dynamicPageItems}
            placeholder={placeholder}
            variant="panel"
          />
        </>
      )}
    </div>
  )
}

function Launcher({
  messageCount,
  isDocked,
  onOpen,
}: {
  messageCount: number
  isDocked: boolean
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex w-full items-center gap-2 rounded-full border border-border bg-backdow-sm backdrop-blur-sm transition-colors bg-background/95 px-4 py-3 text-left sha hover:bg-muted/60",
        !isDocked && "max-w-md",
      )}
    >
      <ChatMessageIcon className="size-4.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-sm font-medium">
        Ask anything about your deals...
      </span>
      {messageCount > 0 && (
        <span className="shrink-0 text-xs text-muted-foreground">{messageCount}</span>
      )}
      <ChevronUp className="size-4 shrink-0 text-muted-foreground" />
    </button>
  )
}

export function ChatPanel({ className, demoMessages, demoAction, demoLoading }: ChatPanelProps) {
  const { open: sidebarOpen } = useSidebar()
  const isMobile = useIsMobile()
  const {
    currentSession,
    isOpen,
    setIsOpen,
    openPanel,
    closePanel,
    layoutMode,
    dockAnchor,
    panelSize,
  } = useChatPanel()
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const dockRef = useRef<HTMLDivElement>(null)
  const isDemoMode = !!demoMessages

  // Auto-open in demo mode
  useEffect(() => {
    if (isDemoMode && !isOpen) {
      openPanel()
    }
  }, [isDemoMode]) // eslint-disable-line react-hooks/exhaustive-deps

  const demoProps = isDemoMode ? { demoMessages, demoAction, demoLoading } : {}

  const effectiveMode = useMemo<ChatLayoutMode>(() => {
    if (!isMobile) {
      return layoutMode
    }

    return layoutMode === "sidebar" ? "sidebar" : "modal"
  }, [isMobile, layoutMode])

  useEffect(() => {
    if (effectiveMode !== "dock") {
      document.documentElement.style.setProperty("--chat-bottom-space", "0px")
      return
    }

    const updateBottomSpace = () => {
      const height = dockRef.current?.offsetHeight ?? 0
      document.documentElement.style.setProperty(
        "--chat-bottom-space",
        `${height + CHAT_BOTTOM_OFFSET}px`,
      )
    }

    updateBottomSpace()

    const observer = new ResizeObserver(updateBottomSpace)
    if (dockRef.current) {
      observer.observe(dockRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [effectiveMode, isOpen, panelSize])

  useEffect(() => {
    if (effectiveMode === "sidebar" && isOpen && !isMobile) {
      document.documentElement.style.setProperty(
        "--app-chat-sidebar-width",
        `var(--app-chat-sidebar-width-${panelSize})`,
      )
      return
    }

    document.documentElement.style.setProperty("--app-chat-sidebar-width", "0px")

    return () => {
      document.documentElement.style.setProperty("--app-chat-sidebar-width", "0px")
    }
  }, [effectiveMode, isMobile, isOpen, panelSize])

  useEffect(() => {
    if (!isOpen) {
      setIsHistoryOpen(false)
    }
  }, [isOpen])

  const dockMaxWidthClass = panelSize === "large" ? "md:max-w-3xl" : "md:max-w-2xl"
  const sheetWidthClass = panelSize === "large"
    ? "sm:max-w-[var(--app-chat-sidebar-width-large)]"
    : "sm:max-w-[var(--app-chat-sidebar-width-medium)]"
  const modalWidthClass = panelSize === "large" ? "max-w-5xl" : "max-w-4xl"

  const dockWrapperClasses = cn(
    "pointer-events-none fixed bottom-4 z-50 left-0 right-0 px-4 md:right-[var(--right-panel-width,0px)]",
    sidebarOpen ? "md:left-[var(--sidebar-width)]" : "md:left-[4rem]",
    className,
  )

  const dockJustifyClasses = dockAnchor === "left"
    ? "justify-start"
    : dockAnchor === "right"
      ? "justify-end"
      : "justify-center"

  if (effectiveMode === "dock") {
    return (
      <div className={dockWrapperClasses}>
        <div className={cn("flex", dockJustifyClasses)}>
          <motion.div
            layout
            ref={dockRef}
            className={cn("pointer-events-auto w-full", dockMaxWidthClass)}
            transition={{ type: "spring", stiffness: 280, damping: 28, layout: { duration: 0.3, type: "spring", bounce: 0 } }}
          >
            {isOpen ? (
              <div style={{ height: CHAT_HEIGHTS[panelSize] }}>
                <PanelBody
                  isHistoryOpen={isHistoryOpen}
                  onHistoryOpenChange={setIsHistoryOpen}
                  onClose={closePanel}
                  {...demoProps}
                  showDockControls={true}
                />
              </div>
            ) : (
              <Launcher
                messageCount={currentSession.messages.length}
                isDocked={true}
                onOpen={openPanel}
              />
            )}
          </motion.div>
        </div>
      </div>
    )
  }

  const launcher = (
    <div className={cn(
      "pointer-events-none fixed bottom-4 left-0 right-0 z-50 flex justify-center px-4 md:right-[var(--right-panel-width,0px)]",
      sidebarOpen ? "md:left-[var(--sidebar-width)]" : "md:left-[4rem]",
      className,
    )}>
      <div className="pointer-events-auto w-full max-w-md">
        {!isOpen && (
          <Launcher
            messageCount={currentSession.messages.length}
            isDocked={false}
            onOpen={openPanel}
          />
        )}
      </div>
    </div>
  )

  return (
    <>
      {launcher}

      {effectiveMode === "sidebar" ? (
        isOpen ? (
          isMobile ? (
            <div className="fixed inset-0 z-50 pointer-events-none">
              <button
                type="button"
                aria-label="Close chat sidebar"
                onClick={closePanel}
                className="pointer-events-auto absolute inset-0 bg-black/32 transition-opacity"
              />

              <motion.div
                initial={{ opacity: 0, x: 32 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 32 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className={cn(
                  "pointer-events-auto absolute right-4 top-4 bottom-4 left-4",
                  "sm:left-auto sm:w-full",
                  sheetWidthClass,
                )}
              >
                <div
                  className={cn(
                    "h-full w-full overflow-hidden rounded-3xl",
                    "border border-border bg-transparent",
                  )}
                >
                  <PanelBody
                    isHistoryOpen={isHistoryOpen}
                    onHistoryOpenChange={setIsHistoryOpen}
                    onClose={closePanel}
                    {...demoProps}
                    showDockControls={false}
                  />
                </div>
              </motion.div>
            </div>
          ) : (
            <motion.aside
              className={cn(
                "fixed inset-y-0 right-0 z-40 border-l border-border bg-background/95 backdrop-blur-sm",
                "transition-[width] duration-300 ease-out-expo",
                "w-(--app-chat-sidebar-width)",
              )}
            >
              <div className="h-full w-full overflow-hidden">
                <PanelBody
                  isHistoryOpen={isHistoryOpen}
                  onHistoryOpenChange={setIsHistoryOpen}
                  onClose={closePanel}
                  {...demoProps}
                  showDockControls={false}
                />
              </div>
            </motion.aside>
          )
        ) : null
      ) : (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogPopup
            className={cn(
              "max-h-[calc(100dvh-2rem)] overflow-hidden p-0",
              modalWidthClass,
            )}
            showCloseButton={false}
          >
            <div style={{ height: isMobile ? "min(80dvh, 720px)" : CHAT_HEIGHTS[panelSize] }}>
              <PanelBody
                isHistoryOpen={isHistoryOpen}
                onHistoryOpenChange={setIsHistoryOpen}
                onClose={closePanel}
                {...demoProps}
                showDockControls={false}
              />
            </div>
          </DialogPopup>
        </Dialog>
      )}
    </>
  )
}


// chat messsage icon
function ChatMessageIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path fill="var(--gradient-blue-purple-to)" d="M20.2405 4.18518L19.5436 2.37334C19.4571 2.14842 19.241 2 19 2C18.759 2 18.5429 2.14842 18.4564 2.37334L17.7595 4.18518C17.658 4.44927 17.4493 4.65797 17.1852 4.75955L15.3733 5.45641C15.1484 5.54292 15 5.75901 15 6C15 6.24099 15.1484 6.45708 15.3733 6.54359L17.1852 7.24045C17.4493 7.34203 17.658 7.55073 17.7595 7.81482L18.4564 9.62666C18.5429 9.85158 18.759 10 19 10C19.241 10 19.4571 9.85158 19.5436 9.62666L20.2405 7.81482C20.342 7.55073 20.5507 7.34203 20.8148 7.24045L22.6267 6.54359C22.8516 6.45708 23 6.24099 23 6C23 5.75901 22.8516 5.54292 22.6267 5.45641L20.8148 4.75955C20.5507 4.65797 20.342 4.44927 20.2405 4.18518Z" />
      <path fill="var(--gradient-blue-purple-from)" d="M16.6074 3L16.4385 3.43848L14.835 4.05664C14.0308 4.36595 13.5 5.13836 13.5 6C13.5 6.86164 14.0308 7.63405 14.835 7.94336L16.4385 8.56055L17.0566 10.165C17.366 10.9692 18.1384 11.5 19 11.5C19.8616 11.5 20.634 10.9692 20.9434 10.165L21.5605 8.56055L22 8.39062V15.25C22 17.3211 20.3211 19 18.25 19H15.376C15.3178 19 15.2616 19.0205 15.2168 19.0576L13.1182 20.7959C12.4677 21.3348 11.5254 21.3317 10.8779 20.7891L8.8125 19.0586C8.76749 19.0209 8.7101 19 8.65137 19H5.75C3.67893 19 2 17.3211 2 15.25V6.75C2 4.67893 3.67893 3 5.75 3H16.6074Z" />
      {/* <path fill="red" d="M15.3733 5.45641C15.3453 5.46721 15.3185 5.48049 15.293 5.49512L15.292 5.49414C15.3176 5.47943 15.3452 5.46725 15.3733 5.45641L15.8643 5.2666L17.1852 4.75955L15.3733 5.45641Z" /> */}
    </svg>
  )
}
